import { map } from 'lodash';
import type { IObjectiveArchive } from './ObjectiveArchive.interface';
import ObjectiveApplicant from './ObjectiveApplicant';

export default class ObjectiveArchive implements IObjectiveArchive {
  objectivesAndLoanFeatures: {
    notes: string,
    immediateNeedsAndObjectivesNotes: string,
    longerTermNotes: string,
    purchaseHome: boolean,
    refinanceHome: boolean,
    otherOwnerOccupied: boolean,
    purchaseInvestment: boolean,
    refinanceInvestment: boolean,
    otherInvestment: boolean,
    refinanceComments: '',
    variableRate: boolean,
    fixedRate: boolean,
    multipleSplits: boolean,
    additionalPayments: boolean,
    redraw: boolean,
    lineOfCredit: boolean,
    offsetAccount: boolean,
    loanVariations: boolean,
    portability: boolean,
    internetBanking: boolean,
    topUp: boolean,
    switchLoans: boolean,
    rateLock: boolean,
    bridgingFinance: boolean,
    fortnightlyRepayments: boolean,
    interestOnly: boolean,
    lendersMortgageInsurance: boolean,
    interestInAdvance: boolean,
    fixedRateComments: string,
    isAware: boolean,
    lineOfCreditComments: string,
    interestOnlyReason: string,
    interestOnlyReasonDetails: string,
    lendersMortgageInsuranceComments: string,
    interestInAdvanceComments: string,
    loanSplits1: string,
    loanSplits2: string,
    loanSplits3: string,
    preferredLenders: string,
    notLenders: string,
    loanAmount: number,
    loanTerm: string,
    repayments: string,
    interestRates: string,
    creditRecord: number,
    creditRecordComments: string
  };

  financialSecurity: {
    levelOfFinancialExperience: string,
    concernedAboutMovementsInInterestRates: string,
    concernedAboutMovementsInInterestRatesComments: string,
    importantFlexibilityOfLoan: string,
    importantFlexibilityOfLoanComments: string,
    concernedAboutYourCurrentJobSecurity: string,
    concernedAboutYourCurrentJobSecurityComments: string,
    concernedAboutNegativePropertyValue: string,
    concernedAboutNegativePropertyValueComments: string,
    expectChangeToYourIncomeSituation: boolean,
    expectChangeToYourIncomeSituationComments: string,
    expectYourExpensesToIncrease: boolean,
    expectYourExpensesToIncreaseComments: string,
    haveAnEmergencyFund: boolean,
    ableToMaintainYourCommitmentsAndLifestyle: boolean,
    haveAdequateInsurance: boolean,
    haveWill: boolean,
    awareOfAnyCircumstances: boolean,
    awareOfAnyCircumstancesComments: string,
    applicants: Array<ObjectiveApplicant>,
    comments: string
  };

  creditHistory: {
    applicantHadProblems: boolean,
    applicantHadProblemsComments: string,
    applicantBeenOfficerOrShareholder: boolean,
    applicantBeenOfficerOrShareholderComments: string,
    applicantHaveUnsatisfiedJudgements: boolean,
    applicantHaveUnsatisfiedJudgementsComments: string,
    applicantSubmittedApplicationToOtherCreditProviders: boolean,
    applicantSubmittedApplicationToOtherCreditProvidersComments: string,
    applicantBeenSubjectToPersonalBankruptcyArrangement: boolean,
    applicantBeenSubjectToPersonalBankruptcyArrangementComments: string
  };

  additionalServices: {
    insuranceAndAssetProtection: boolean,
    investmentSuperannuationAndFinancialPlanning: boolean,
    propertyBuyingAdvocacy: boolean,
    propertyInvestment: boolean,
    carBuyingAdvocacyOrFinance: boolean,
    conveyancing: boolean,
    other: boolean,
    otherComments: string
  };

  health: {
    applicants: Array<ObjectiveApplicant>
  };

  static build = (data: IObjectiveArchive) => new ObjectiveArchive(data);
  static blank = () => new ObjectiveArchive();

  /**
   * Constructor
   * @param {IObjectiveArchive} data
   */
  constructor(data?: IObjectiveArchive) {
    this.objectivesAndLoanFeatures = {
      notes: data?.objectivesAndLoanFeatures?.notes || '',
      immediateNeedsAndObjectivesNotes:
        data?.objectivesAndLoanFeatures?.immediateNeedsAndObjectivesNotes || '',
      longerTermNotes: data?.objectivesAndLoanFeatures?.longerTermNotes || '',
      purchaseHome: data?.objectivesAndLoanFeatures?.purchaseHome === true,
      refinanceHome: data?.objectivesAndLoanFeatures?.refinanceHome === true,
      otherOwnerOccupied: data?.objectivesAndLoanFeatures?.otherOwnerOccupied === true,
      purchaseInvestment: data?.objectivesAndLoanFeatures?.purchaseInvestment === true,
      refinanceInvestment: data?.objectivesAndLoanFeatures?.refinanceInvestment === true,
      otherInvestment: data?.objectivesAndLoanFeatures?.otherInvestment === true,
      refinanceComments: '',
      variableRate: data?.objectivesAndLoanFeatures?.variableRate === true,
      fixedRate: data?.objectivesAndLoanFeatures?.fixedRate === true,
      multipleSplits: data?.objectivesAndLoanFeatures?.multipleSplits === true,
      additionalPayments: data?.objectivesAndLoanFeatures?.additionalPayments === true,
      redraw: data?.objectivesAndLoanFeatures?.redraw === true,
      lineOfCredit: data?.objectivesAndLoanFeatures?.lineOfCredit === true,
      offsetAccount: data?.objectivesAndLoanFeatures?.offsetAccount === true,
      loanVariations: data?.objectivesAndLoanFeatures?.loanVariations === true,
      portability: data?.objectivesAndLoanFeatures?.portability === true,
      internetBanking: data?.objectivesAndLoanFeatures?.internetBanking === true,
      topUp: data?.objectivesAndLoanFeatures?.topUp === true,
      switchLoans: data?.objectivesAndLoanFeatures?.switchLoans === true,
      rateLock: data?.objectivesAndLoanFeatures?.rateLock === true,
      bridgingFinance: data?.objectivesAndLoanFeatures?.bridgingFinance === true,
      fortnightlyRepayments: data?.objectivesAndLoanFeatures?.fortnightlyRepayments === true,
      interestOnly: data?.objectivesAndLoanFeatures?.interestOnly === true,
      lendersMortgageInsurance:
        data?.objectivesAndLoanFeatures?.lendersMortgageInsurance === true,
      interestInAdvance: data?.objectivesAndLoanFeatures?.interestInAdvance === true,
      fixedRateComments: data?.objectivesAndLoanFeatures?.fixedRateComments || '',
      isAware: data?.objectivesAndLoanFeatures?.isAware === true,
      lineOfCreditComments: data?.objectivesAndLoanFeatures?.lineOfCreditComments || '',
      interestOnlyReason: data?.objectivesAndLoanFeatures?.interestOnlyReason || '',
      interestOnlyReasonDetails:
        data?.objectivesAndLoanFeatures?.interestOnlyReasonDetails || '',
      lendersMortgageInsuranceComments:
        data?.objectivesAndLoanFeatures?.lendersMortgageInsuranceComments || '',
      interestInAdvanceComments:
        data?.objectivesAndLoanFeatures?.interestInAdvanceComments || '',
      loanSplits1: data?.objectivesAndLoanFeatures?.loanSplits1 || '',
      loanSplits2: data?.objectivesAndLoanFeatures?.loanSplits2 || '',
      loanSplits3: data?.objectivesAndLoanFeatures?.loanSplits3 || '',
      preferredLenders: data?.objectivesAndLoanFeatures?.preferredLenders || '',
      notLenders: data?.objectivesAndLoanFeatures?.notLenders || '',
      loanAmount: data?.objectivesAndLoanFeatures?.loanAmount || 0,
      loanTerm: data?.objectivesAndLoanFeatures?.loanTerm || '',
      repayments: data?.objectivesAndLoanFeatures?.repayments || '',
      interestRates: data?.objectivesAndLoanFeatures?.interestRates || '',
      creditRecord: data?.objectivesAndLoanFeatures?.creditRecord || 0,
      creditRecordComments: data?.objectivesAndLoanFeatures?.creditRecordComments || ''
    };
    this.financialSecurity = {
      levelOfFinancialExperience: data?.financialSecurity?.levelOfFinancialExperience || '',
      concernedAboutMovementsInInterestRates:
        data?.financialSecurity?.concernedAboutMovementsInInterestRates || '',
      concernedAboutMovementsInInterestRatesComments:
        data?.financialSecurity?.concernedAboutMovementsInInterestRatesComments || '',
      importantFlexibilityOfLoan: data?.financialSecurity?.importantFlexibilityOfLoan || '',
      importantFlexibilityOfLoanComments:
        data?.financialSecurity?.importantFlexibilityOfLoanComments || '',
      concernedAboutYourCurrentJobSecurity:
        data?.financialSecurity?.concernedAboutYourCurrentJobSecurity || '',
      concernedAboutYourCurrentJobSecurityComments:
        data?.financialSecurity?.concernedAboutYourCurrentJobSecurityComments || '',
      concernedAboutNegativePropertyValue:
        data?.financialSecurity?.concernedAboutNegativePropertyValue || '',
      concernedAboutNegativePropertyValueComments:
        data?.financialSecurity?.concernedAboutNegativePropertyValueComments || '',
      expectChangeToYourIncomeSituation:
        data?.financialSecurity?.expectChangeToYourIncomeSituation === true,
      expectChangeToYourIncomeSituationComments:
        data?.financialSecurity?.expectChangeToYourIncomeSituationComments || '',
      expectYourExpensesToIncrease:
        data?.financialSecurity?.expectYourExpensesToIncrease === true,
      expectYourExpensesToIncreaseComments:
        data?.financialSecurity?.expectYourExpensesToIncreaseComments || '',
      haveAnEmergencyFund: data?.financialSecurity?.haveAnEmergencyFund === true,
      ableToMaintainYourCommitmentsAndLifestyle:
        data?.financialSecurity?.ableToMaintainYourCommitmentsAndLifestyle === true,
      haveAdequateInsurance: data?.financialSecurity?.haveAdequateInsurance === true,
      haveWill: data?.financialSecurity?.haveWill === true,
      awareOfAnyCircumstances: data?.financialSecurity?.awareOfAnyCircumstances === true,
      awareOfAnyCircumstancesComments:
        data?.financialSecurity?.awareOfAnyCircumstancesComments || '',
      applicants: map(data?.applicants, (obj) => new ObjectiveApplicant(obj)),
      comments: data?.financialSecurity?.comments || ''
    };
    this.creditHistory = {
      applicantHadProblems: data?.creditHistory?.applicantHadProblems === true,
      applicantHadProblemsComments: data?.creditHistory?.applicantHadProblemsComments || '',
      applicantBeenOfficerOrShareholder:
        data?.creditHistory?.applicantBeenOfficerOrShareholder === true,
      applicantBeenOfficerOrShareholderComments:
        data?.creditHistory?.applicantBeenOfficerOrShareholderComments || '',
      applicantHaveUnsatisfiedJudgements:
        data?.creditHistory?.applicantHaveUnsatisfiedJudgements === true,
      applicantHaveUnsatisfiedJudgementsComments:
        data?.creditHistory?.applicantHaveUnsatisfiedJudgementsComments || '',
      applicantSubmittedApplicationToOtherCreditProviders:
        data?.creditHistory?.applicantSubmittedApplicationToOtherCreditProviders === true,
      applicantSubmittedApplicationToOtherCreditProvidersComments:
        data?.creditHistory?.applicantSubmittedApplicationToOtherCreditProvidersComments || '',
      applicantBeenSubjectToPersonalBankruptcyArrangement:
        data?.creditHistory?.applicantBeenSubjectToPersonalBankruptcyArrangement === true,
      applicantBeenSubjectToPersonalBankruptcyArrangementComments:
        data?.creditHistory?.applicantBeenSubjectToPersonalBankruptcyArrangementComments || ''
    };
    this.additionalServices = {
      insuranceAndAssetProtection:
        data?.additionalServices?.insuranceAndAssetProtection === true,
      investmentSuperannuationAndFinancialPlanning:
        data?.additionalServices?.investmentSuperannuationAndFinancialPlanning === true,
      propertyBuyingAdvocacy: data?.additionalServices?.propertyBuyingAdvocacy === true,
      propertyInvestment: data?.additionalServices?.propertyInvestment === true,
      carBuyingAdvocacyOrFinance: data?.additionalServices?.carBuyingAdvocacyOrFinance === true,
      conveyancing: data?.additionalServices?.conveyancing === true,
      other: data?.additionalServices?.other === true,
      otherComments: data?.additionalServices?.otherComments || ''
    };
    this.health = {
      applicants: map(data?.applicants, (obj) => new ObjectiveApplicant(obj))
    };
  }
}
